import { createContext, useContext, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { jwtDecode } from "jwt-decode";
import Swal from 'sweetalert2'

const AuthContext = createContext()

export function AuthProvider({ children }) {

    const [user, setUser] = useState(null)
    const [token, setToken] = useState(null)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()

    const parseStoredToken = (storedToken) => {
        if (!storedToken) return null
        try {
            const parsed = JSON.parse(storedToken)
            return typeof parsed === 'string' ? parsed : null
        } catch {
            return typeof storedToken === 'string' ? storedToken : null
        }
    }

    const getUsuarioFromToken = (rawToken) => {
        const decoded = jwtDecode(rawToken)
        return {
            id: decoded?.role?.id ?? decoded?.user_id,
            rol: decoded?.role?.nombre,
            nombre: decoded?.role?.usuario || decoded?.username || ''
        }
    }

    useEffect(() => {
        const storedToken = localStorage.getItem("token");
        const tokenPersistido = parseStoredToken(storedToken)
        if (isValid(tokenPersistido)) {
            setToken(tokenPersistido);
            const usuario = getUsuarioFromToken(tokenPersistido)
            setUser(usuario)
            navigate("/home", { state: { usuario: usuario } });
        }
    }, []);

    const isValid = (token) => {
        if (!token || typeof token !== 'string') return false
        try {
            return !isTokenExpired(token)
        } catch {
            return false
        }
    }

    const isTokenExpired = (token) => {
        const decoded = jwtDecode(token)
        return (decoded.exp < Date.now() / 1000)
    }

    const logout = () => {
        if (user.rol === "Docente") {
            setUser(null)
            localStorage.removeItem("token")
            navigate("/")
        } else {
            setUser(null)
            localStorage.removeItem("token")
            navigate("/evaluador")
        }
    }

    const login = (email, password) => {
        setLoading(true)
        fetch(`${process.env.REACT_APP_API_URL}/token/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: email, password: password })
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Error en la solicitud')
                }
                return response.json()
            })
            .then((res) => {
                setToken(res.access)
                const usuario = getUsuarioFromToken(res.access)
                setUser(usuario)
                localStorage.setItem("token", JSON.stringify(res.access))
                navigate("/home", { state: { usuario: usuario } });
            })
            .catch((error) => {
                console.log(error)
                setError(error.message)
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Usuario o contraseña incorrecta",
                    confirmButtonText: 'Cerrar',
                    confirmButtonColor: '#972034'
                });
            })
            .finally(() => {
                setLoading(false)
                setError(null)
            })
    }

    const loginSAML = (token) => {
        const tokenNormalizado = typeof token === 'string' ? token.trim() : ''
        if (!tokenNormalizado) {
            setError('Token SAML inválido')
            navigate('/')
            return
        }

        setToken(tokenNormalizado)
        const usuario = getUsuarioFromToken(tokenNormalizado)
        setUser(usuario)
        localStorage.setItem("token", JSON.stringify(tokenNormalizado))
        navigate("/home", { state: { usuario: usuario } });
    }

    return (
        <AuthContext.Provider value={{ user, login, loginSAML, logout, token, error, setError, isTokenExpired, loading }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    return useContext(AuthContext)
}
